/**
 * Product-page store availability.
 * After window load, GET /stock?sku= and render available_sizes.
 * A later check requests /stock?sku=&size= for each selected size.
 */
(() => {
  const DAY_TO_NUMBER = {
    sunday: 0,
    sun: 0,
    0: 0,
    monday: 1,
    mon: 1,
    1: 1,
    tuesday: 2,
    tue: 2,
    2: 2,
    wednesday: 3,
    wed: 3,
    3: 3,
    thursday: 4,
    thu: 4,
    4: 4,
    friday: 5,
    fri: 5,
    5: 5,
    saturday: 6,
    sat: 6,
    6: 6,
    ראשון: 0,
    שני: 1,
    שלישי: 2,
    רביעי: 3,
    חמישי: 4,
    שישי: 5,
    שבת: 6,
  };

  function parseConfig(script) {
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  }

  function resolveSku(value) {
    const raw = String(value || "").trim();
    const match = raw.match(/^(\d{9})/);
    return match ? match[1] : raw;
  }

  const MOCK_STOCK_STORES = [
    { storeDescription: "פקטורי 54 רמת אביב", availability: "lowStock" },
    { storeDescription: "פקטורי 54 דיזנגוף", availability: "inStock" },
    { storeDescription: "פקטורי 54 קניון TLV", availability: "lowStock" },
  ];

  function shouldUseMock(config) {
    if (config?.useMock) return true;
    try {
      return new URLSearchParams(window.location.search).has(
        "store_availability_mock",
      );
    } catch (error) {
      return false;
    }
  }

  function mockStockPayload(sku, size) {
    return {
      success: true,
      sku: sku || "862100498",
      selected_size: size || "",
      available_sizes: ["39", "40", "41", "42", "43", "44", "45", "46", "47"],
      stores: size ? MOCK_STOCK_STORES : [],
    };
  }

  function buildStockUrl(apiBase, sku, size) {
    const trimmed = String(apiBase || "").trim();
    const absolute = /^https?:\/\//i.test(trimmed)
      ? trimmed.replace(/\/$/, "")
      : new URL(trimmed || "/stock", window.location.origin)
          .toString()
          .replace(/\/$/, "");
    const endpoint = /\/stock$/i.test(absolute) ? absolute : `${absolute}/stock`;
    const url = new URL(endpoint);
    url.searchParams.set("sku", sku);
    if (size) url.searchParams.set("size", size);
    return url.toString();
  }

  function asObject(value) {
    return value && typeof value === "object" && !Array.isArray(value)
      ? value
      : null;
  }

  function looksLikeStore(item) {
    if (!item || typeof item !== "object" || Array.isArray(item)) return false;
    return (
      item.storeDescription != null ||
      item.store_description != null ||
      item.storeNumber != null ||
      item.store_number != null ||
      item.StoreNum != null ||
      item.storeId != null ||
      item.store_id != null ||
      item.branch != null ||
      item.branchNumber != null ||
      item.number != null ||
      item.storeName != null ||
      item.store_name != null ||
      (item.name && (item.address || item.city || item.phone || item.sizes))
    );
  }

  function extractAvailableSizes(payload) {
    const root = asObject(payload);
    if (!root) return [];

    const candidates = [
      root.available_sizes,
      root.availableSizes,
      root.data?.available_sizes,
      root.data?.availableSizes,
      root.result?.available_sizes,
      root.result?.availableSizes,
    ];

    for (const candidate of candidates) {
      const sizes = normalizeSizeList(candidate);
      if (sizes) return sizes;
    }

    return [];
  }

  function normalizeSizeList(candidate) {
    if (Array.isArray(candidate)) {
      const sizes = candidate.map((size) => String(size).trim()).filter(Boolean);
      return sizes.length ? sizes : [];
    }

    if (typeof candidate === "string" && candidate.trim()) {
      return candidate
        .split(/[,/]/)
        .map((size) => size.trim())
        .filter(Boolean);
    }

    return null;
  }

  function extractStoreList(payload) {
    if (Array.isArray(payload)) {
      return payload.filter(looksLikeStore);
    }

    const root = asObject(payload);
    if (!root) return [];

    const candidates = [
      root.stores,
      root.branches,
      root.locations,
      root.results,
      root.items,
      root.data,
      root.result,
      root.data?.stores,
      root.data?.branches,
      root.result?.stores,
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate) && candidate.some(looksLikeStore)) {
        return candidate.filter(looksLikeStore);
      }
      if (looksLikeStore(candidate)) return [candidate];
    }

    return looksLikeStore(root) ? [root] : [];
  }

  function readStoreNumber(store) {
    const value =
      store?.storeNumber ??
      store?.store_number ??
      store?.StoreNum ??
      store?.storeId ??
      store?.store_id ??
      store?.branchNumber ??
      store?.branch_number ??
      store?.branch ??
      store?.number ??
      store?.id ??
      store?.handle;
    if (value == null || value === "") return "";
    return String(value).trim();
  }

  function readStoreName(store) {
    return String(
      store?.storeDescription ||
        store?.store_description ||
        store?.name ||
        store?.storeName ||
        store?.store_name ||
        store?.title ||
        "",
    ).trim();
  }

  function normalizeStoreText(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/factory\s*54|פקטורי\s*54/g, "f54")
      .replace(/פקטורי|factory/g, "f54")
      .replace(/[^\p{L}\p{N}\s]+/gu, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  const GENERIC_STORE_TOKENS = new Set([
    "f54",
    "54",
    "men",
    "women",
    "mall",
    "קניון",
    "חנות",
    "store",
  ]);

  function significantTokens(value) {
    return normalizeStoreText(value)
      .split(" ")
      .filter((token) => token.length >= 2 && !GENERIC_STORE_TOKENS.has(token));
  }

  function catalogSearchText(store) {
    return [store?.name, store?.address, store?.city, store?.handle]
      .filter(Boolean)
      .join(" ");
  }

  function scoreStoreMatch(query, store) {
    const normalizedQuery = normalizeStoreText(query);
    const haystack = normalizeStoreText(catalogSearchText(store));
    if (!normalizedQuery || !haystack) return 0;
    if (haystack === normalizedQuery) return 100;
    if (haystack.includes(normalizedQuery) || normalizedQuery.includes(haystack)) {
      return 90;
    }

    const tokens = significantTokens(query);
    if (!tokens.length) return 0;
    const hits = tokens.filter((token) => haystack.includes(token));
    return (hits.length / tokens.length) * 80;
  }

  function normalizeKey(value) {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");
  }

  function parseTimeToHHmm(value) {
    const raw = String(value || "").trim();
    if (!raw) return null;
    const colon = raw.match(/^(\d{1,2}):(\d{2})$/);
    if (colon) return colon[1].padStart(2, "0") + colon[2];
    const digits = raw.replace(/\D/g, "");
    if (digits.length === 3 || digits.length === 4) return digits.padStart(4, "0");
    return null;
  }

  function parseRange(value) {
    const parts = String(value)
      .split(/-|–|—/)
      .map((part) => part.trim())
      .filter(Boolean);
    if (parts.length < 2) return null;
    const open = parseTimeToHHmm(parts[0]);
    const close = parseTimeToHHmm(parts[1]);
    if (!open || !close) return null;
    return { open, close };
  }

  function isClosedValue(value) {
    const normalized = String(value || "").trim().toLowerCase();
    return !normalized || normalized === "closed" || normalized === "סגור" || normalized === "close";
  }

  function timeToMinutes(timeValue) {
    const normalized = String(timeValue).padStart(4, "0");
    const hours = parseInt(normalized.slice(0, 2), 10);
    const minutes = parseInt(normalized.slice(2, 4), 10);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return NaN;
    return hours * 60 + minutes;
  }

  function rangesToPeriods(day, value) {
    if (isClosedValue(value)) return [];
    return String(value)
      .split(/[,;]+/)
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => parseRange(part))
      .filter(Boolean)
      .map((range) => {
        const openMinutes = timeToMinutes(range.open);
        const closeMinutes = timeToMinutes(range.close);
        let closeDay = day;
        if (Number.isFinite(openMinutes) && Number.isFinite(closeMinutes) && closeMinutes <= openMinutes) {
          closeDay = (day + 1) % 7;
        }
        return {
          open: { day, time: range.open },
          close: { day: closeDay, time: range.close },
        };
      });
  }

  function parseDayKeyedHours(source) {
    const periods = [];
    Object.entries(source || {}).forEach(([key, value]) => {
      const day = DAY_TO_NUMBER[String(key).trim().toLowerCase()];
      if (day == null) return;
      periods.push(...rangesToPeriods(day, value));
    });
    return periods.length ? { periods } : null;
  }

  function parseLineHours(text) {
    const source = {};
    String(text)
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .forEach((line) => {
        const match = line.match(/^([^:]+)\s*:\s*(.+)$/);
        if (!match) return;
        source[match[1].trim()] = match[2].trim();
      });
    return parseDayKeyedHours(source);
  }

  function parseOpeningHours(raw) {
    if (!raw) return null;
    let value = raw;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return null;
      try {
        value = JSON.parse(trimmed);
      } catch (error) {
        return parseLineHours(trimmed);
      }
    }
    if (Array.isArray(value)) return value.length ? { periods: value } : null;
    if (typeof value !== "object") return null;
    if (Array.isArray(value.periods)) {
      return value.periods.length ? { periods: value.periods } : null;
    }
    if (value.opening_hours?.periods) return parseOpeningHours(value.opening_hours);
    if (value.current_opening_hours?.periods) {
      return parseOpeningHours(value.current_opening_hours);
    }
    return parseDayKeyedHours(value);
  }

  function formatGoogleTime(timeValue) {
    const normalized = String(timeValue).padStart(4, "0");
    return `${normalized.slice(0, 2)}:${normalized.slice(2, 4)}`;
  }

  function getZonedParts(timeZone) {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      weekday: "long",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date());
    const getPart = (type) => parts.find((part) => part.type === type)?.value;
    return {
      day: DAY_TO_NUMBER[String(getPart("weekday") || "").toLowerCase()],
      hour: parseInt(getPart("hour"), 10),
      minute: parseInt(getPart("minute"), 10),
    };
  }

  function computeOpenNowFromPeriods(periods, timeZone) {
    if (!Array.isArray(periods) || !periods.length) return null;
    const { day, hour, minute } = getZonedParts(timeZone);
    if (!Number.isFinite(day) || !Number.isFinite(hour) || !Number.isFinite(minute)) {
      return null;
    }
    const WEEK_MINUTES = 7 * 24 * 60;
    const now = day * 24 * 60 + hour * 60 + minute;
    for (const period of periods) {
      if (!period?.open) continue;
      if (!period.close) return true;
      const start = period.open.day * 24 * 60 + timeToMinutes(period.open.time);
      let end = period.close.day * 24 * 60 + timeToMinutes(period.close.time);
      if (!Number.isFinite(start) || !Number.isFinite(end)) continue;
      if (end <= start) end += WEEK_MINUTES;
      if (
        (now >= start && now < end) ||
        (now + WEEK_MINUTES >= start && now + WEEK_MINUTES < end)
      ) {
        return true;
      }
    }
    return false;
  }

  function getHoursDisplay(store, labels, timeZone) {
    if (!store) return null;
    if (
      store.businessStatus === "CLOSED_PERMANENTLY" ||
      store.businessStatus === "CLOSED_TEMPORARILY"
    ) {
      return {
        hoursText: `${labels.today} ${labels.closedToday}`,
        statusText: labels.closed,
        isOpen: false,
      };
    }
    const openingHours = parseOpeningHours(store.openingHours);
    if (!openingHours?.periods?.length) return null;
    const today = getZonedParts(timeZone).day;
    const todayPeriods = openingHours.periods.filter((period) => period.open?.day === today);
    if (!todayPeriods.length) {
      return {
        hoursText: `${labels.today} ${labels.closedToday}`,
        statusText: labels.closed,
        isOpen: false,
      };
    }
    const hoursRanges = todayPeriods.map((period) => {
      const openTime = formatGoogleTime(period.open.time);
      if (!period.close) return openTime;
      return `${openTime}-${formatGoogleTime(period.close.time)}`;
    });
    const computed = computeOpenNowFromPeriods(openingHours.periods, timeZone);
    return {
      hoursText: `${labels.today} ${hoursRanges.join(", ")}`,
      statusText: computed ? labels.open : labels.closed,
      isOpen: computed === true,
    };
  }

  function renderHoursElement(element, display) {
    if (!element) return;
    if (!display) {
      element.hidden = true;
      element.replaceChildren();
      return;
    }

    element.hidden = false;
    element.replaceChildren();
    element.append(display.hoursText);

    const separator = document.createElement("span");
    separator.className = "store-availability__hours-separator";
    separator.setAttribute("aria-hidden", "true");
    separator.textContent = "•";
    element.append(separator);

    const status = document.createElement("span");
    status.className = display.isOpen
      ? "store-availability__status-open"
      : "store-availability__status-closed";
    status.textContent = display.statusText;
    element.append(status);
  }

  function findCatalogStore(catalog, apiStore) {
    const number = normalizeKey(readStoreNumber(apiStore));
    if (number) {
      const strippedNumber = number.replace(/^0+/, "");
      const byNumber = catalog.find((store) => {
        const catalogNumber = normalizeKey(store.number || "");
        const catalogHandle = normalizeKey(store.handle || "");
        const catalogStripped = catalogNumber.replace(/^0+/, "");
        return (
          (catalogNumber && (catalogNumber === number || catalogStripped === strippedNumber)) ||
          (catalogHandle &&
            (catalogHandle === number ||
              catalogHandle.replace(/^0+/, "") === strippedNumber ||
              catalogHandle.endsWith(strippedNumber)))
        );
      });
      if (byNumber) return byNumber;
    }

    const query = readStoreName(apiStore);
    if (!query) return null;

    let best = null;
    let bestScore = 0;
    catalog.forEach((store) => {
      const score = scoreStoreMatch(query, store);
      if (score > bestScore) {
        bestScore = score;
        best = store;
      }
    });
    return bestScore >= 50 ? best : null;
  }

  function mergeStoreRecord(map, apiStore, size, catalog) {
    const catalogStore = findCatalogStore(catalog, apiStore) || {};
    const number = readStoreNumber(catalogStore) || readStoreNumber(apiStore);
    const apiName = catalogStore.name || readStoreName(apiStore);
    const name = catalogStore.displayName || apiName;
    const key = number || normalizeKey(apiName) || `store-${map.size}`;
    const existing = map.get(key) || {
      key,
      number,
      name,
      address: catalogStore.address || apiStore.address || apiStore.storeAddress || "",
      city: catalogStore.city || apiStore.city || "",
      phone: catalogStore.phone || apiStore.phone || "",
      mapLink: catalogStore.mapLink || apiStore.mapLink || apiStore.waze || "",
      openingHours: catalogStore.openingHours,
      businessStatus: catalogStore.businessStatus,
      sizes: [],
    };
    if (size && !existing.sizes.includes(size)) existing.sizes.push(size);
    (apiStore.size || apiStore.Size || apiStore.sizeName || "")
      .toString()
      .split(/[,/]/)
      .map((part) => part.trim())
      .filter(Boolean)
      .forEach((part) => {
        if (!existing.sizes.includes(part)) existing.sizes.push(part);
      });
    map.set(key, existing);
  }

  class StoreAvailability extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.config = parseConfig(this.querySelector("[data-store-availability-config]"));
      this.statusEl = this.querySelector("[data-store-availability-status]");
      this.resultsEl = this.querySelector("[data-store-availability-results]");
      this.listEl = this.querySelector("[data-store-availability-list]");
      this.cardTemplate = this.querySelector("[data-store-availability-card]");
      this.submitButton = this.querySelector(".store-availability__submit");
      this.sizesEl = this.querySelector("[data-store-availability-sizes]");
      this.swatchesEl = this.querySelector("[data-store-availability-swatches]");
      this.sizeInputs = [];
      this.abortController = null;
      this.sizesPromise = null;

      this.submitButton?.addEventListener("click", () => {
        this.checkAvailability();
      });

      this.scheduleAvailableSizes();
    }

    scheduleAvailableSizes() {
      const run = () => {
        if (!this.isConnected) return;
        this.sizesPromise = this.loadAvailableSizes();
      };

      if (document.readyState === "complete") {
        setTimeout(run, 0);
        return;
      }

      window.addEventListener("load", () => setTimeout(run, 0), { once: true });
    }

    async loadAvailableSizes() {
      const sku = resolveSku(this.config?.sku);
      if (!sku && !shouldUseMock(this.config)) return;

      try {
        const payload = await this.fetchStockPayload(sku, "");
        this.renderSizes(extractAvailableSizes(payload));
      } catch (error) {
        this.sizesLoadError = true;
      }
    }

    renderSizes(sizes) {
      if (!this.sizesEl || !this.swatchesEl) return;

      const unique = [];
      sizes.forEach((size) => {
        if (!unique.includes(size)) unique.push(size);
      });

      this.swatchesEl.replaceChildren();
      this.sizeInputs = [];

      if (!unique.length) {
        this.sizesEl.hidden = true;
        this.syncSubmitButton();
        return;
      }

      const autoSelect = unique.length === 1;
      const blockId = this.dataset.blockId || "size";
      unique.forEach((size, index) => {
        const slug =
          size.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || String(index);
        const id = `store-availability-${blockId}-${slug}`;
        const input = document.createElement("input");
        input.className = "sr-only";
        input.type = "checkbox";
        input.name = `store-availability-size-${blockId}`;
        input.id = id;
        input.value = size;
        input.checked = autoSelect;
        input.setAttribute("data-store-availability-size", "");
        input.addEventListener("change", () => {
          input.nextElementSibling?.classList.toggle("is-selected", input.checked);
          this.syncSubmitButton();
        });

        const label = document.createElement("label");
        label.className = autoSelect ? "block-swatch is-selected" : "block-swatch";
        label.htmlFor = id;
        const text = document.createElement("span");
        text.textContent = size;
        label.append(text);
        this.swatchesEl.append(input, label);
        this.sizeInputs.push(input);
      });

      this.sizesEl.hidden = autoSelect;
      this.syncSubmitButton();
    }

    syncSubmitButton() {
      if (!this.submitButton) return;
      this.submitButton.disabled = this.waitingForSize();
    }

    waitingForSize() {
      return this.sizeInputs.length > 0 && this.selectedSizes().length === 0;
    }

    selectedSizes() {
      return this.sizeInputs.filter((input) => input.checked).map((input) => input.value);
    }

    setStatus(message, isError) {
      if (!this.statusEl) return;
      if (!message) {
        this.statusEl.hidden = true;
        this.statusEl.textContent = "";
        this.statusEl.classList.remove("is-error");
        return;
      }
      this.statusEl.hidden = false;
      this.statusEl.textContent = message;
      this.statusEl.classList.toggle("is-error", Boolean(isError));
    }

    setLoading(isLoading) {
      if (!this.submitButton || !this.config?.labels) return;
      this.submitButton.disabled = isLoading || this.waitingForSize();
      this.submitButton.textContent = isLoading
        ? this.config.labels.checking
        : this.config.labels.check;
    }

    async fetchStockPayload(sku, size, signal) {
      if (shouldUseMock(this.config)) {
        return mockStockPayload(sku, size);
      }

      const response = await fetch(buildStockUrl(this.config.apiUrl, sku, size), {
        method: "GET",
        headers: { Accept: "application/json" },
        signal,
      });
      const payload = await response.json().catch(() => null);
      if (payload?.Forbidden) {
        throw new Error("forbidden");
      }
      if (response.status === 404) return payload || {};
      if (!response.ok || payload?.success === false) {
        if (!size && extractAvailableSizes(payload).length) return payload;
        throw new Error(`stock ${response.status}`);
      }
      return payload || {};
    }

    async fetchStock(sku, size, signal) {
      const payload = await this.fetchStockPayload(sku, size, signal);
      return extractStoreList(payload);
    }

    renderStores(stores) {
      if (!this.listEl || !this.cardTemplate || !this.resultsEl) return;
      this.listEl.replaceChildren();

      if (!stores.length) {
        this.resultsEl.hidden = true;
        return;
      }

      const labels = this.config.labels;
      const sizeLineTemplate = labels.sizeLine || "Size: {{ size }}";

      stores.forEach((store) => {
        const fragment = this.cardTemplate.content.cloneNode(true);
        const nameEl = fragment.querySelector("[data-store-name]");
        const addressEl = fragment.querySelector("[data-store-address]");
        const sizesEl = fragment.querySelector("[data-store-sizes]");
        const hoursEl = fragment.querySelector("[data-store-hours]");
        const actionsEl = fragment.querySelector("[data-store-actions]");
        const navigateEl = fragment.querySelector("[data-store-navigate]");
        const phoneEl = fragment.querySelector("[data-store-phone]");

        if (nameEl) nameEl.textContent = store.name || "";
        if (addressEl) {
          const address = store.address || store.city || "";
          addressEl.textContent = address;
          addressEl.hidden = !address;
        }

        if (sizesEl) {
          if (!store.sizes.length) {
            sizesEl.hidden = true;
          } else {
            sizesEl.hidden = false;
            sizesEl.textContent = sizeLineTemplate.replace(
              /\{\{\s*size\s*\}\}/g,
              store.sizes.join(", "),
            );
          }
        }

        renderHoursElement(
          hoursEl,
          getHoursDisplay(store, labels, this.config.hoursTimezone || "Asia/Jerusalem"),
        );

        if (navigateEl && store.mapLink) {
          navigateEl.hidden = false;
          navigateEl.href = store.mapLink;
        } else if (navigateEl) {
          navigateEl.hidden = true;
        }

        if (phoneEl && store.phone) {
          phoneEl.hidden = false;
          phoneEl.href = `tel:${String(store.phone).replace(/[\s-]/g, "")}`;
        } else if (phoneEl) {
          phoneEl.hidden = true;
        }

        if (actionsEl) {
          const hasNavigate = navigateEl && !navigateEl.hidden;
          const hasPhone = phoneEl && !phoneEl.hidden;
          actionsEl.hidden = !(hasNavigate || hasPhone);
        }

        this.listEl.append(fragment);
      });

      this.resultsEl.hidden = false;
    }

    async checkAvailability() {
      const labels = this.config?.labels || {};
      const sku = resolveSku(this.config?.sku);

      if (this.sizesPromise) await this.sizesPromise;

      const sizes = this.selectedSizes();
      const hasSizePicker = this.sizeInputs.length > 0;

      if (!sku && !shouldUseMock(this.config)) {
        this.setStatus(labels.noSku, true);
        this.renderStores([]);
        return;
      }

      if (this.sizesLoadError && !hasSizePicker) {
        this.setStatus(labels.error, true);
        this.renderStores([]);
        return;
      }

      if (hasSizePicker && sizes.length === 0) {
        this.setStatus(labels.selectSize, true);
        this.renderStores([]);
        return;
      }

      this.abortController?.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      const catalog = this.config.stores || [];
      const merged = new Map();

      this.setLoading(true);
      this.setStatus("");

      try {
        const queries = hasSizePicker ? sizes : [""];
        const results = await Promise.all(
          queries.map(async (size) => {
            try {
              const stores = await this.fetchStock(sku, size, signal);
              return { size, stores };
            } catch (error) {
              if (error?.name === "AbortError") throw error;
              return { size, stores: [], failed: true };
            }
          }),
        );

        const anySuccess = results.some((result) => !result.failed);
        if (!anySuccess) {
          throw new Error("stock failed");
        }

        results.forEach(({ size, stores }) => {
          stores.forEach((store) => mergeStoreRecord(merged, store, size, catalog));
        });

        const list = Array.from(merged.values()).sort((a, b) =>
          String(a.name).localeCompare(String(b.name), "he"),
        );
        this.renderStores(list);
        if (!list.length) this.setStatus(labels.noStores, false);
      } catch (error) {
        if (error?.name === "AbortError") return;
        this.renderStores([]);
        this.setStatus(labels.error, true);
      } finally {
        this.setLoading(false);
      }
    }
  }

  if (!customElements.get("store-availability")) {
    customElements.define("store-availability", StoreAvailability);
  }
})();
